import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { cn, customImageLoader, getAssetSrc } from '@restworld/utils-common';

import { useReferralCredits } from './use-referral-credits';

const REASONS = [
  {
    titleKey: 'reason_1_title',
    bodyKey: 'reason_1_body',
    image: '/images/referral/reason-1.png',
    rotation: -10.26,
  },
  {
    titleKey: 'reason_2_title',
    bodyKey: 'reason_2_body',
    image: '/images/referral/reason-2.png',
    rotation: 6.65,
  },
  {
    titleKey: 'reason_3_title',
    bodyKey: 'reason_3_body',
    image: '/images/referral/reason-3.png',
    rotation: -5.57,
  },
];

/**
 * "Why should I invite someone?" section with 3 reason cards, each having
 * a rotated decorative image, a short title, and a supporting paragraph.
 */
export function ReferralWhyInvite() {
  const { t, i18n } = useTranslation('referral-page');
  const { inviter } = useReferralCredits();
  // Exact saving per invite, in cents to avoid float drift: 3 days -> 59,70 (it) / 59.70 (en).
  const inviterSavings = formatEuroCents(inviter * PPU_DAY_PRICE_CENTS, i18n.language);

  return (
    <SectionContainer>
      <SectionTitle>{t('why_invite_title')}</SectionTitle>

      <ReasonsCard>
        <ReasonsGrid>
          {REASONS.map((reason) => (
            <ReasonItem key={reason.titleKey}>
              <ImageWrapper>
                <RotatedImage style={{ transform: `rotate(${reason.rotation}deg)` }}>
                  <Image
                    className="rounded-2xl object-cover"
                    style={{ width: '128px', height: '128px' }}
                    src={getAssetSrc(reason.image)}
                    alt=""
                    width={128}
                    height={128}
                    loader={customImageLoader}
                  />
                </RotatedImage>
              </ImageWrapper>
              <ReasonCopy>
                <ReasonTitle>{t(reason.titleKey, { inviterSavings })}</ReasonTitle>
                <ReasonBody>{t(reason.bodyKey, { inviter })}</ReasonBody>
              </ReasonCopy>
            </ReasonItem>
          ))}
        </ReasonsGrid>
      </ReasonsCard>
    </SectionContainer>
  );
}

export default ReferralWhyInvite;

/** Price of one PPU day in cents. */
const PPU_DAY_PRICE_CENTS = 1990;

/** Formats cents as an amount with two decimals, comma-separated for Italian. */
export function formatEuroCents(cents: number, language?: string): string {
  const amount = `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
  return language?.startsWith('en') ? amount : amount.replace('.', ',');
}

// Layout Components
const SectionContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-center w-full py-10 px-4 lg:px-20 gap-10', className)} {...props} />
);

const SectionTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h2 className={cn('text-xl font-semibold text-[#1C1C33] leading-[1.21]', className)} {...props} />
);

const ReasonsCard = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('w-full p-6 rounded-2xl border border-[#6633BB]', className)}
    style={{
      background: 'rgba(102, 51, 187, 0.05)',
      boxShadow: '0px 2px 4px 0px rgba(0, 0, 26, 0.05)',
    }}
    {...props}
  />
);

const ReasonsGrid = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col lg:flex-row gap-6 w-full', className)} {...props} />
);

const ReasonItem = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex-1 flex flex-row items-center gap-2.5', className)} {...props} />
);

const ImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('flex items-center justify-center flex-shrink-0', className)}
    style={{ width: '148px', height: '148px' }}
    {...props}
  />
);

// Brand-purple tint veil over the image via an `::after` overlay: `mix-blend-color`
// keeps the image's light/shadow and only shifts the hue. Tweak `after:opacity-50`
// (0 = original colors, 1 = full purple).
const RotatedImage = ({ className, style, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'rounded-2xl overflow-hidden relative',
      "after:content-[''] after:absolute after:inset-0 after:bg-[#6633bb] after:mix-blend-color after:opacity-50 after:rounded-[inherit] after:pointer-events-none",
      className
    )}
    style={{ width: '128px', height: '128px', ...style }}
    {...props}
  />
);

const ReasonCopy = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-1 flex-col gap-1', className)} {...props} />
);

const ReasonTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h3 className={cn('text-sm font-bold text-[#1C1C33] m-0', className)} style={{ lineHeight: '22px' }} {...props} />
);

const ReasonBody = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-sm font-medium text-[#1C1C33] m-0', className)} style={{ lineHeight: '22px' }} {...props} />
);
